/**
 * Declarative tab chrome for MiniModal (pill header + Swiper panels).
 *
 * Usage:
 * ```tsx
 * <MiniModalTabs isDarkUi={isDarkUi} activeTabId={id} onActiveTabChange={setId}>
 *   <MiniModalTab id=\"bike\" label=\"Vélo\" iconSrc={...}>
 *     ...
 *   </MiniModalTab>
 * </MiniModalTabs>
 * ```
 */

import React, {
    Children,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type ReactElement,
    type ReactNode,
} from "react";
import { FreeMode, Mousewheel } from "swiper/modules";
import { Swiper, SwiperSlide, type SwiperClass } from "swiper/react";
import { useReducedMotion } from "../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { useMiniModalTabs } from "./useMiniModalTabs.hook";

export interface MiniModalTabDefinition {

    /** Stable tab identifier used for selection and ARIA. */
    "id": string;

    /** Visible label in the pill header. */
    "label": string;

    /** Optional icon displayed inside the pill button. */
    "iconSrc"?: string;

    /** Optional Tailwind classes overriding the default tab icon size. */
    "iconClassName"?: string;

    /** Panel body rendered inside the swipeable slide. */
    "panel": ReactNode;
}

export interface MiniModalTabProps {
    "id":             string;
    "label":          string;
    "iconSrc"?:       string;
    "iconClassName"?: string;
    "children":       ReactNode;
}

/**
 * Declarative tab panel to be used as a child of {@link MiniModalTabs}.
 * Renders nothing by itself; it is parsed by the parent.
 */
export const MiniModalTab: React.FC<MiniModalTabProps> = () => {
    return null;
};

const isMiniModalTabElement = ( node: unknown ): node is ReactElement<MiniModalTabProps> => {
    return Boolean( node != null &&
      typeof node === "object" &&
      "type" in ( node as Record<string, unknown> ) &&
      ( node as { "type": unknown } ).type === MiniModalTab );
};

interface MiniModalTabsProps {

    /** Enables dark palette variants for tab chrome. */
    "isDarkUi": boolean;

    /** Controlled active tab id. If omitted, the component is uncontrolled. */
    "activeTabId"?: string;

    /** Initial tab id when uncontrolled. Defaults to the first declared tab. */
    "defaultTabId"?: string;

    /** Called when the user selects a different tab (tap or swipe). */
    "onActiveTabChange"?: ( tabId: string ) => void;

    /** Optional accessible name for the tab list. */
    "ariaLabel"?: string;

    /** Optional class names on the root wrapper. */
    "className"?: string;

    /** When `true`, pill headers show only icons; the active tab also shows its label inline. */
    "iconOnly"?: boolean;

    /** Declarative tab declarations. */
    "children": ReactNode;
}

const TAB_TRANSITION_MS = 200;
const SWIPER_SPEED_MS = 280;

/** Pixel geometry of one tab pill used to position the sliding indicator. */
interface TabIndicatorRect {
    "left":  number;
    "width": number;
}

/**
 * Interpolates indicator geometry between two measured tab rects.
 *
 * @param lowerRect Rect at the lower tab index.
 * @param upperRect Rect at the upper tab index.
 * @param fraction Blend ratio in `[0, 1]`.
 * @returns Interpolated left offset and width in pixels.
 */
const interpolateTabIndicatorRect = (
    lowerRect: TabIndicatorRect,
    upperRect: TabIndicatorRect,
    fraction: number,
): TabIndicatorRect => {
    return {
        "left":  lowerRect.left + ( upperRect.left - lowerRect.left ) * fraction,
        "width": lowerRect.width + ( upperRect.width - lowerRect.width ) * fraction,
    };
};

/**
 * Resolves indicator geometry for a fractional tab index from measured rects.
 *
 * @param tabIndicatorRects Measured pill geometry per tab.
 * @param fractionalIndex Continuous index (0 … tabCount − 1).
 * @returns Indicator left offset and width, or `null` when measurement is unavailable.
 */
const resolveIndicatorRectAtIndex = (
    tabIndicatorRects: readonly TabIndicatorRect[],
    fractionalIndex: number,
): TabIndicatorRect | null => {
    if ( tabIndicatorRects.length === 0 ) {
        return null;
    }

    const clampedIndex = Math.min( tabIndicatorRects.length - 1, Math.max( 0, fractionalIndex ) );
    const lowerIndex = Math.floor( clampedIndex );
    const upperIndex = Math.min( tabIndicatorRects.length - 1, Math.ceil( clampedIndex ) );
    const lowerRect = tabIndicatorRects[lowerIndex];
    const upperRect = tabIndicatorRects[upperIndex];

    if ( lowerRect === undefined || upperRect === undefined ) {
        return null;
    }

    if ( lowerIndex === upperIndex ) {
        return lowerRect;
    }

    return interpolateTabIndicatorRect( lowerRect, upperRect, clampedIndex - lowerIndex );
};

/**
 * Resolves the index of a tab id within the tab list.
 *
 * @param tabs Tab definitions.
 * @param tabId Candidate tab id.
 * @returns Zero-based index, or `-1` when not found.
 */
const getTabIndex = ( tabs: readonly MiniModalTabDefinition[], tabId: string ): number => {
    return tabs.findIndex( ( tab ) => tab.id === tabId );
};

const MiniModalTabs: React.FC<MiniModalTabsProps> = ( {
    isDarkUi,
    "activeTabId": controlledActiveTabId,
    defaultTabId,
    onActiveTabChange,
    ariaLabel = "Sections",
    className = "",
    iconOnly = false,
    children,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );
    const reducedMotion = useReducedMotion();
    const swiperRef = useRef<SwiperClass | null>( null );
    const panelRefs = useRef<Array<HTMLDivElement | null>>( [] );
    const tabListRef = useRef<HTMLDivElement | null>( null );
    const tabButtonRefs = useRef<Array<HTMLButtonElement | null>>( [] );
    const [ panelMinHeightPx, setPanelMinHeightPx ] = useState<number>( 0 );
    const [ tabIndicatorRects, setTabIndicatorRects ] = useState<readonly TabIndicatorRect[]>( [] );

    const tabs = useMemo<readonly MiniModalTabDefinition[]>( () => {
        const declared = Children.toArray( children ).filter( isMiniModalTabElement );

        return declared.map( ( element ) => ( {
            "id":            element.props.id,
            "label":         element.props.label,
            "iconSrc":       element.props.iconSrc,
            "iconClassName": element.props.iconClassName,
            "panel":         element.props.children,
        } ) );
    }, [ children ] );

    const { activeTabId, setActiveTabId } = useMiniModalTabs( {
        "tabs":                tabs,
        "activeTabId":         controlledActiveTabId,
        "defaultTabId":        defaultTabId,
        "onActiveTabIdChange": onActiveTabChange,
        "enableTabHaptics":    true,
    } );

    const activeIndex = useMemo( (): number => {
        const index = getTabIndex( tabs, activeTabId );
        if ( index >= 0 ) {
            return index;
        }

        return 0;
    }, [ activeTabId, tabs ] );
    const [ indicatorIndex, setIndicatorIndex ] = useState<number>( activeIndex );

    const slideSpeed = reducedMotion ? 0 : SWIPER_SPEED_MS;
    const indicatorTransition = reducedMotion
        ? "none"
        : `left ${ TAB_TRANSITION_MS }ms cubic-bezier(0.2, 0.8, 0.2, 1), width ${ TAB_TRANSITION_MS }ms cubic-bezier(0.2, 0.8, 0.2, 1), transform ${ TAB_TRANSITION_MS }ms cubic-bezier(0.2, 0.8, 0.2, 1)`;

    /**
     * Measures each tab pill so the sliding indicator can match the active segment width.
     */
    const measureTabIndicatorRects = useCallback( (): void => {
        const tabListElement = tabListRef.current;
        if ( tabListElement === null ) {
            return;
        }

        const listRect = tabListElement.getBoundingClientRect();
        const nextRects = tabButtonRefs.current.map( ( tabButton ) => {
            if ( tabButton === null ) {
                return {
                    "left":  0,
                    "width": 0,
                };
            }

            const buttonRect = tabButton.getBoundingClientRect();

            return {
                "left":  buttonRect.left - listRect.left,
                "width": buttonRect.width,
            };
        } );

        setTabIndicatorRects( nextRects );
    }, [] );

    const equalWidthIndicatorStyle: CSSProperties = useMemo( () => {
        const tabCount = Math.max( tabs.length, 1 );
        const widthPercent = 100 / tabCount;

        return {
            "width":           `${ widthPercent }%`,
            "transform":       `translateX(${ indicatorIndex * 100 }%)`,
            "transition":      indicatorTransition,
            "backgroundColor": palette.selectorActiveBackgroundStrong,
        };
    }, [ indicatorIndex, indicatorTransition, palette.selectorActiveBackgroundStrong, tabs.length ] );

    const flexIndicatorStyle: CSSProperties = useMemo( () => {
        const measuredRect = resolveIndicatorRectAtIndex( tabIndicatorRects, indicatorIndex );

        if ( measuredRect === null ) {
            return equalWidthIndicatorStyle;
        }

        return {
            "left":            `${ measuredRect.left }px`,
            "width":           `${ measuredRect.width }px`,
            "transform":       "none",
            "transition":      indicatorTransition,
            "backgroundColor": palette.selectorActiveBackgroundStrong,
        };
    }, [
        equalWidthIndicatorStyle,
        indicatorIndex,
        indicatorTransition,
        palette.selectorActiveBackgroundStrong,
        tabIndicatorRects,
    ] );

    const indicatorStyle = iconOnly ? flexIndicatorStyle : equalWidthIndicatorStyle;

    /**
     * Handles pill tap: sync Swiper and notify MiniModal-owned state.
     *
     * @param tabId Target tab id.
     * @param index Tab index for Swiper.
     */
    const handleTabSelect = useCallback( ( tabId: string, index: number ): void => {
        swiperRef.current?.slideTo( index, slideSpeed );
        setActiveTabId( tabId );
    }, [ setActiveTabId, slideSpeed ] );

    /**
     * Keeps the tab indicator synchronized with continuous Swiper progress.
     * This is required for wheel/free-mode interactions where `onSliderMove`
     * is not always fired like touch dragging.
     *
     * @param swiper Active Swiper instance.
     */
    const syncIndicatorFromProgress = useCallback( ( swiper: SwiperClass ): void => {
        if ( tabs.length <= 1 ) {
            setIndicatorIndex( 0 );

            return;
        }

        const fractionalIndex = swiper.progress * ( tabs.length - 1 );
        setIndicatorIndex( Math.min( tabs.length - 1, Math.max( 0, fractionalIndex ) ) );
    }, [ tabs.length ] );

    /**
     * Keeps Swiper index aligned when the parent updates `activeTabId` (e.g. persisted reopen).
     */
    useEffect( () => {
        const index = getTabIndex( tabs, activeTabId );
        if ( index < 0 ) {
            return;
        }

        if ( swiperRef.current !== null && swiperRef.current.activeIndex !== index ) {
            swiperRef.current.slideTo( index, slideSpeed );
        }
    }, [ activeTabId, slideSpeed, tabs ] );

    /** Keeps the indicator snapped to the selected tab outside of drag interactions. */
    useEffect( () => {
        setIndicatorIndex( activeIndex );
    }, [ activeIndex ] );

    /** Re-measures tab pills when the active segment expands or contracts. */
    useLayoutEffect( () => {
        if ( !iconOnly ) {
            return;
        }

        measureTabIndicatorRects();
        const animationFrameId = window.requestAnimationFrame( () => {
            measureTabIndicatorRects();
        } );

        if ( typeof ResizeObserver === "undefined" ) {
            return () => {
                window.cancelAnimationFrame( animationFrameId );
            };
        }

        const resizeObserver = new ResizeObserver( () => {
            measureTabIndicatorRects();
        } );

        const tabListElement = tabListRef.current;
        if ( tabListElement !== null ) {
            resizeObserver.observe( tabListElement );
        }

        tabButtonRefs.current.forEach( ( tabButton ) => {
            if ( tabButton !== null ) {
                resizeObserver.observe( tabButton );
            }
        } );

        return () => {
            window.cancelAnimationFrame( animationFrameId );
            resizeObserver.disconnect();
        };
    }, [ activeIndex, iconOnly, measureTabIndicatorRects, tabs ] );

    /**
     * Keeps a stable swipe hit area when tabs have different content heights.
     * Without this, swiping in the visual empty space below short tabs does not trigger.
     */
    useLayoutEffect( () => {
        const computeMaxPanelHeight = (): void => {
            const maxHeight = panelRefs.current.reduce( ( currentMax, panelRef ) => {
                if ( panelRef === null ) {
                    return currentMax;
                }

                return Math.max( currentMax, panelRef.getBoundingClientRect().height );
            }, 0 );

            setPanelMinHeightPx( Math.ceil( maxHeight ) );
        };

        computeMaxPanelHeight();

        if ( typeof ResizeObserver === "undefined" ) {
            return;
        }

        const resizeObserver = new ResizeObserver( () => {
            computeMaxPanelHeight();
        } );

        panelRefs.current.forEach( ( panelRef ) => {
            if ( panelRef !== null ) {
                resizeObserver.observe( panelRef );
            }
        } );

        return () => {
            resizeObserver.disconnect();
        };
    }, [ tabs ] );

    if ( tabs.length === 0 ) {
        return null;
    }

    const gridTemplateColumns = `repeat(${ tabs.length }, minmax(0, 1fr))`;

    const tabButtons = tabs.map( ( tab, index ) => {
        const isSelected = index === activeIndex;
        const tabStyle: CSSProperties = { "color": isSelected ? palette.selectorActiveText : palette.selectorIdleText };
        const defaultIconClassName = iconOnly ? "h-4 w-4" : "h-3.5 w-3.5";
        const resolvedIconClassName = tab.iconClassName ?? defaultIconClassName;
        const showInlineLabel = iconOnly && isSelected;

        return (
            <button
                aria-label={iconOnly && !isSelected ? tab.label : undefined}
                aria-selected={isSelected}
                className={
                    iconOnly
                        ? isSelected
                            ? "relative z-10 flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 transition-opacity duration-150 hover:opacity-90"
                            : "relative z-10 flex shrink-0 items-center justify-center rounded-full px-1.5 py-1.5 transition-opacity duration-150 hover:opacity-90"
                        : "relative z-10 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-opacity duration-150 hover:opacity-90"
                }
                id={`mini-modal-tab-${ tab.id }`}
                key={tab.id}
                onClick={() => {
                    handleTabSelect( tab.id, index );
                }}
                ref={( element ) => {
                    tabButtonRefs.current[index] = element;
                }}
                role="tab"
                style={tabStyle}
                type="button"
            >
                <span className={
                    iconOnly
                        ? `inline-flex min-w-0 items-center justify-center ${ isSelected ? "gap-1.5" : "" }`
                        : "inline-flex min-w-0 items-center justify-center gap-1"
                }
                >
                    {tab.iconSrc !== undefined && tab.iconSrc.length > 0 && (
                        <img
                            alt=""
                            aria-hidden
                            className={`${ resolvedIconClassName } block shrink-0 object-contain`}
                            src={tab.iconSrc}
                        />
                    )}
                    {showInlineLabel && (
                        <span className="whitespace-nowrap text-xs font-semibold uppercase tracking-wide">
                            {tab.label}
                        </span>
                    )}
                    {!iconOnly && tab.label}
                </span>
            </button>
        );
    } );

    const tabListClassName = iconOnly
        ? "relative flex items-stretch rounded-full border p-0.5"
        : "relative grid rounded-full border p-0.5";

    const tabListStyle: CSSProperties = iconOnly
        ? {
            "borderColor":     palette.overlayBorder,
            "backgroundColor": palette.selectorIdleBackground,
        }
        : {
            "borderColor":         palette.overlayBorder,
            "backgroundColor":     palette.selectorIdleBackground,
            "gridTemplateColumns": gridTemplateColumns,
        };

    const indicatorTrackClassName = iconOnly
        ? "pointer-events-none absolute inset-y-0.5 left-0.5 right-0.5 overflow-hidden rounded-full"
        : "pointer-events-none absolute inset-0.5 overflow-hidden rounded-full";

    const indicatorElementClassName = iconOnly
        ? "absolute top-0 bottom-0 rounded-full"
        : "h-full rounded-full";

    return (
        <div className={className}>
            <div
                aria-label={ariaLabel}
                className={tabListClassName}
                ref={tabListRef}
                role="tablist"
                style={tabListStyle}
            >
                <div
                    aria-hidden
                    className={indicatorTrackClassName}
                >
                    <div
                        className={indicatorElementClassName}
                        style={indicatorStyle}
                    />
                </div>
                {tabButtons}
            </div>
            <div
                className="mt-3"
                style={{
                    "minHeight":           panelMinHeightPx > 0 ? `${ panelMinHeightPx }px` : undefined,
                    "overscrollBehavior":  "none",
                    "overscrollBehaviorX": "none",
                }}
            >
                <Swiper
                    allowTouchMove
                    freeMode={{
                        "enabled":  true,
                        "sticky":   true,
                        "momentum": false,
                    }}
                    modules={[ Mousewheel, FreeMode ]}
                    mousewheel={{
                        "enabled":        true,
                        "forceToAxis":    true,
                        "releaseOnEdges": false,
                        "sensitivity":    0.45,
                    }}
                    onProgress={( swiper ) => {
                        syncIndicatorFromProgress( swiper );
                    }}
                    onSlideChange={( swiper ) => {
                        const nextTab = tabs[swiper.activeIndex];
                        if ( nextTab === undefined ) {
                            return;
                        }

                        setActiveTabId( nextTab.id );
                    }}
                    onSliderMove={( swiper ) => {
                        syncIndicatorFromProgress( swiper );
                    }}
                    onSwiper={( swiper ) => {
                        swiperRef.current = swiper;
                        if ( swiper.activeIndex !== activeIndex ) {
                            swiper.slideTo( activeIndex, 0 );
                        }
                    }}
                    resistanceRatio={0.65}
                    slidesPerView={1}
                    speed={slideSpeed}
                    style={{ "minHeight": panelMinHeightPx > 0 ? `${ panelMinHeightPx }px` : undefined }}
                    threshold={8}
                >
                    {tabs.map( ( tab ) => (
                        <SwiperSlide
                            key={tab.id}
                            style={{ "minHeight": panelMinHeightPx > 0 ? `${ panelMinHeightPx }px` : undefined }}
                        >
                            <div
                                aria-labelledby={`mini-modal-tab-${ tab.id }`}
                                ref={( element ) => {
                                    const panelIndex = getTabIndex( tabs, tab.id );
                                    if ( panelIndex >= 0 ) {
                                        panelRefs.current[panelIndex] = element;
                                    }
                                }}
                                role="tabpanel"
                            >
                                {tab.panel}
                            </div>
                        </SwiperSlide>
                    ) )}
                </Swiper>
            </div>
        </div>
    );
};

export default MiniModalTabs;
